import { type PostingWindow, resolveNextOpenSlots } from './next-open-slot';

import type { SchedulingPreferences } from '@/services/data/UserPreferencesService';

/**
 * The one definition of "no preference set yet".
 *
 * Previously a literal inside SchedulingSettings.tsx. Auto-scheduling has to
 * agree with what the settings screen shows the user, and two copies of the
 * same default drift the moment one of them is edited — so the settings screen
 * imports this rather than the reverse (a lib module must not import a
 * settings component).
 */
export const DEFAULT_SCHEDULING_PREFERENCES: SchedulingPreferences = {
  earliestHour: 7,
  latestHour: 21,
  activeDays: [1, 2, 3, 4, 5, 6, 0],
};

/** Never place a post so close to now that the user can't react to it. */
export const AUTO_SCHEDULE_LEAD_MINUTES = 60;

function isUsable(prefs: SchedulingPreferences | null | undefined): prefs is SchedulingPreferences {
  if (!prefs) return false;
  const { earliestHour, latestHour, activeDays } = prefs;
  if (!Array.isArray(activeDays) || activeDays.length === 0) return false;
  if (!Number.isInteger(earliestHour) || !Number.isInteger(latestHour)) return false;
  if (earliestHour < 0 || latestHour > 23 || earliestHour > latestHour) return false;
  return true;
}

/**
 * Bridge the user's posting WINDOW (an hour range + active days) to the
 * engine's discrete weekly OCCURRENCES.
 *
 * One occurrence per hour in the range, per active day: the engine then picks
 * the soonest one that is free, which reads to the user as "the next open time
 * inside the hours I said I post." Emitting a single occurrence per day would
 * instead make every collision cost a whole day.
 */
function windowsForPrefs(prefs: SchedulingPreferences): PostingWindow[] {
  const windows: PostingWindow[] = [];
  for (const day of prefs.activeDays) {
    if (!Number.isInteger(day) || day < 0 || day > 6) continue;
    for (let hour = prefs.earliestHour; hour <= prefs.latestHour; hour++) {
      windows.push({ day, hour, minute: 0 });
    }
  }
  return windows;
}

/** Precomputed once: DEFAULT_SCHEDULING_PREFERENCES is a known-good constant
 *  (every active day, a valid hour range) that always yields a non-empty
 *  window list, so the fallback below never needs to recurse to compute it.
 *  Frozen (and never returned by reference elsewhere) because it's a shared
 *  module-level array — a future caller that sorts/mutates a returned windows
 *  array would otherwise corrupt this default for every other caller. Named
 *  distinctly from `./next-open-slot`'s own exported `DEFAULT_POSTING_WINDOWS`
 *  (a different, 3-window constant) since this module already imports from
 *  that one — same name, different module, would have been a real trap. */
const DEFAULT_PREFS_POSTING_WINDOWS: readonly PostingWindow[] = Object.freeze(
  windowsForPrefs(DEFAULT_SCHEDULING_PREFERENCES).map((w) => Object.freeze(w)),
);

/**
 * Unusable or exhausted prefs fall back to the shared default rather than
 * returning [] — resolveNextOpenSlots THROWS on an empty window array, and
 * this is called from a click handler.
 */
export function schedulingPrefsToPostingWindows(
  prefs: SchedulingPreferences | null | undefined,
): PostingWindow[] {
  if (!isUsable(prefs)) return [...DEFAULT_PREFS_POSTING_WINDOWS];
  const windows = windowsForPrefs(prefs);
  return windows.length > 0 ? windows : [...DEFAULT_PREFS_POSTING_WINDOWS];
}

/**
 * The next open posting slot for ONE post, or null if none is reachable.
 *
 * Null is a real outcome the caller must handle — silently scheduling nothing
 * is the failure mode this codebase has shipped most often.
 */
export function resolveAutoScheduleSlot(args: {
  prefs: SchedulingPreferences | null | undefined;
  taken: Date[];
  now: Date;
  leadMinutes?: number;
  maxHorizonSlots?: number;
}): Date | null {
  const { prefs, taken, now, leadMinutes = AUTO_SCHEDULE_LEAD_MINUTES } = args;
  const windows = schedulingPrefsToPostingWindows(prefs);
  const earliest = new Date(now.getTime() + leadMinutes * 60_000);
  const slots = resolveNextOpenSlots(
    windows,
    taken,
    1,
    earliest,
    args.maxHorizonSlots === undefined ? {} : { maxHorizonSlots: args.maxHorizonSlots },
  );
  return slots[0] ?? null;
}
